<template>
  <v-container>
    <v-dialog
      persistent
      v-model="modifying_dialog"
      transition="slide-y-transition"
      width="100%"
    >
      <div :style="{display: 'flex', justifyContent: 'center', alignItems: 'center'}">
        <create-issue :ticket="ticket" :modifying_ticket="true" @close-form="modifying_dialog=false; menu_down=false"/>
      </div>
    </v-dialog>
    <v-card flat class="pa-0 ma-0">
      <v-row class="align-center text-center" style="text-align: center" justify="center">
        <v-col cols="6" md="1">
          <p class="d-md-none">{{ `ID: ` }}</p>
          <h4>{{ ticket['id'] }}</h4>
        </v-col>
        <v-col
          :style="{ color: ticket['priority'] === 1 ? 'red' : ticket['priority'] === 2 ? 'orange' : 'green' }"
          cols="6" md="1"
        >
          <h4>{{ park_name }}</h4>
        </v-col>
        <v-col cols="6" md="1"><p class="d-md-none">{{ `${t('opened')}: ` }}</p>
          <h4>{{ new Date(ticket['start_date']).toLocaleDateString('it-IT') }}</h4>
        </v-col>
        <v-col cols="6" md="1"><p class="d-md-none">{{ `${t('creator')}: ` }}</p>{{ editor }}</v-col>
        <v-col cols="6" md="2">
          <p class="d-md-none">{{ `${t('title')}: ` }}</p>
          <h4>
            {{ ticket['title'] }}
          </h4>
        </v-col>
        <v-col
          class="d-flex justify-center align-center"
          cols="6" md="1"
        >
          <v-checkbox disabled v-model="ticket['is_solved']">
            <p class="d-md-none">{{ `${t('is_solved')}` }}</p>
          </v-checkbox>
        </v-col>
        <v-col cols="6" md="1">
          <p class="d-md-none">{{ `${t('intervention_date')}: ` }}</p>
          <h4>{{
              ticket['resolution_date'] ? new Date(ticket['resolution_date']).toLocaleDateString('it-IT') : 'not planned'
            }}</h4>
        </v-col>
        <v-col cols="6" md="1"><p class="d-md-none">{{ `${t('agency')}: ` }}</p><h4>{{ agency_name }}</h4></v-col>
        <v-col cols="6" md="2"><p class="d-md-none">{{ `${t('address')}: ` }}</p><h4>{{ full_address }}</h4></v-col>
        <v-col cols="6" md="1">
          <p class="d-md-none">{{ `${t('actions')}: ` }}</p>
          <v-btn :icon="true" variant="tonal" size="small">
            <v-icon icon="mdi-chevron-down"/>
            <v-menu activator="parent" v-model="menu_down">
              <v-list density="comfortable">
                <v-list-item
                  @click="modifying_dialog = true"
                >
                  <template v-slot:append>
                    <v-icon icon="mdi-pencil"></v-icon>
                  </template>
                  {{ t('modify') }}
                </v-list-item>
                <v-list-item
                  @click="console.log('delete dialog for ticket with id: ', ticket['id'])"
                >
                  <template v-slot:append>
                    <v-icon icon="mdi-close-circle"></v-icon>
                  </template>
                  <v-dialog
                    persistent
                    activator="parent"
                    v-model="deleting_dialog"
                    width="unset"
                    transition="slide-y-transition"
                  >
                    <v-card width="400px">
                      <v-card-title>{{ "Confirm Delete" }}</v-card-title>
                      <v-card-text>
                        {{ t('are_you_sure') }}
                        <h3>{{ t('cannot_be_undone') }}</h3>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn color="secondary" @click="deleting_dialog=false; menu_down=false">{{
                            t('cancel')
                          }}
                        </v-btn>
                        <v-spacer/>
                        <v-btn color="red darken-1" @click="delete_ticket">{{ t('delete') }}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  {{ t('delete') }}
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script setup>
import { onMounted, ref, toRefs, watch } from 'vue';
import { endpoint } from "../issues_utils.js";
import { useI18n } from "vue-i18n";
import { useIssueStore } from "../store/issues_store.js";
import axios from "axios";
import CreateIssue from "./CreateIssue.vue";

const store = useIssueStore();
const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

const park_name = ref('');
const agency_name = ref('');
const full_address = ref('');
const editor = ref('');
const menu_down = ref(false);
const deleting_dialog = ref(false);
const modifying_dialog = ref(false);

const props = defineProps({
  ticket: {
    type: Object,
    required: true
  },
  is_open: {
    type: Boolean,
    required: true
  }
});

const { park_list, agencies_with_issues, staffers, displayed_tickets } = toRefs(store);

const delete_ticket = async () => {
  console.log(`Deleting ticket ${props.ticket['id']}...`);
  try {
    const res = await axios.delete(endpoint(`internal/current/tickets/${props.ticket['id']}/`), { withCredentials: true });
    if (res.status === 204) {
      alert("Ticket deleted successfully!");
      store.ticket_list = [];
      await store.load_tickets();
      menu_down.value = false;
      deleting_dialog.value = false;
    } else {
      alert("Failed to delete ticket!");
    }
  } catch (e) {
    console.log(`Error deleting ticket: ${e.message}`);
    throw e;
  }
};

async function refresh() {
  const edit = staffers.value.find(s => s['id'] === props.ticket['made_by']);
  editor.value = edit ? edit['username'] : 'Unknown';
  try {
    // await store.load_parks();
    const park = park_list.value.find(p => p['id'] === props.ticket['park']);
    park_name.value = park['name'];
    const park_data = store.parks_with_issues.find(p => p.id === park['id']);
    const address = park_data['address'];
    const city = park_data['city'];
    const postal_code = park_data['postal_code'];
    full_address.value = `${address}, ${city} ${postal_code}`;
    const agency_id = park_data['agency'];
    if (agency_id) {
      const agency = agencies_with_issues.value.find(a => a.id === agency_id);
      agency_name.value = agency['name'];
    } else {
      agency_name.value = 'Not assigned';
    }
  } catch (e) {
    console.log(e.toString());
    throw e;
  }
}

watch(() => props.ticket, async () => {
  await refresh();
}, {
  deep: true,
  immediate: true
});

onMounted(() => {
  let open = false;
  if (props.is_open !== undefined) {
    open = props.is_open;
  }
  modifying_dialog.value = open;
});

</script>

<i18n>
{
  "en": {
    "modify": "Modify",
    "delete": "Delete",
    "are_you_sure": "Are you sure you want to delete this issue?",
    "cannot_be_undone": "This action cannot be undone.",
    "cancel": "Cancel",
    "issue_list": "Issue List",
    "new_issue": "New Issue",
    "edit_issue": "Edit Issue",
    "park_name": "Park Name",
    "priority": "Priority",
    "opened": "Issue opened",
    "creator": "Creator",
    "is_solved": "Solved?",
    "title": "Issue title",
    "intervention_date": "Maintenance date",
    "agency": "Agency",
    "address": "Address of the park",
    "actions": "Actions",
    "load_tickets": "Reload Issues"
  },
  "it": {
    "modify": "Modifica",
    "delete": "Elimina",
    "are_you_sure": "Sei sicuro di voler eliminare questo ticket?",
    "cannot_be_undone": "Questa azione non può essere annullata.",
    "cancel": "Annulla",
    "issue_list": "Lista dei tickets",
    "new_issue": "Nuovo ticket",
    "edit_issue": "Modifica ticket",
    "park_name": "Nome parco",
    "priority": "Priorità",
    "opened": "Ticket aperto il",
    "creator": "Creatore",
    "is_solved": "Risolto?",
    "title": "Titolo del problema",
    "intervention_date": "Data manutenzione",
    "agency": "Agenzia",
    "address": "Indirizzo del parco",
    "actions": "Azioni",
    "load_tickets": "Ricarica i Tickets"
  },
  "fr": {
    "modify": "Modifier",
    "delete": "Supprimer",
    "are_you_sure": "Etes-vous sûr de vouloir supprimer ce ticket?",
    "cannot_be_undone": "Cette action ne peut pas être annulée.",
    "cancel": "Annuler",
    "issue_list": "Liste des problèmes",
    "new_issue": "Nouveau problème",
    "edit_issue": "Modifier problème",
    "park_name": "Nom du parc",
    "priority": "Priorité",
    "opened": "Il problème a commencé le",
    "creator": "Créateur",
    "is_solved": "Est-ce résolu?",
    "title": "Titre du problème",
    "intervention_date": "Date de maintenance",
    "agency": "Agence",
    "address": "Adresse du parc",
    "actions": "Actions",
    "load_tickets": "Rafraîchir le problèmes"
  },
  "nl": {
    "modify": "Wijzig",
    "delete": "Verwijder",
    "are_you_sure": "Weet je zeker dat je deze melding wilt verwijderen?",
    "cannot_be_undone": "Deze actie kan niet ongedaan worden gemaakt.",
    "cancel": "Annuleren",
    "issue_list": "Probleemlijst",
    "new_issue": "Nieuw probleem",
    "edit_issue": "Bewerk probleem",
    "park_name": "Parkeernaam",
    "priority": "Prioriteit",
    "opened": "Het probleem begon op",
    "creator": "Auteur",
    "is_solved": "Is dit opgelost?",
    "title": "Titel van het probleem",
    "intervention_date": "Datum onderhoud",
    "agency": "Organisatie",
    "address": "Adres van de parkeerplaats",
    "actions": "Acties",
    "load_tickets": "Laad tickets"
  }
}
</i18n>

<style scoped>

</style>
